import { brandNameFromDomain } from "./brandProfileConstants";
import { formatCompetitorEntry, parseCompetitorAliases, parseCompetitorDomain } from "./probeDomain";

export const COMPANY_SCALES = ["small", "medium", "large"] as const;
export type CompanyScale = (typeof COMPANY_SCALES)[number];

export type CompanyScaleTone = {
    label: string;
    hint: string;
    /** Tiny "Small / Medium / Large" pill on a row. */
    badge: string;
    /** Unselected catalog chip under "Add from…". */
    chip: string;
    swatch: string;
};

export const COMPANY_SCALE_META: Record<CompanyScale, CompanyScaleTone> = {
    small: {
        label: "Small",
        hint: "Startups & SMBs",
        badge: "bg-teal-100 text-teal-800",
        chip: "border-teal-200 bg-teal-50 text-teal-800 hover:border-teal-400 hover:text-teal-900",
        swatch: "bg-teal-400"
    },
    medium: {
        label: "Medium",
        hint: "Mid-market",
        badge: "bg-sky-100 text-sky-800",
        chip: "border-sky-200 bg-sky-50 text-sky-800 hover:border-sky-400 hover:text-sky-900",
        swatch: "bg-sky-400"
    },
    large: {
        label: "Large",
        hint: "Enterprise",
        badge: "bg-violet-100 text-violet-800",
        chip: "border-violet-200 bg-violet-50 text-violet-800 hover:border-violet-400 hover:text-violet-900",
        swatch: "bg-violet-400"
    }
};

/** Catalog scale for a domain, or null when the user typed it in (keep neutral). */
export function competitorScaleFromCatalog(
    domain: string,
    byScale?: Record<CompanyScale, { domain: string }[]> | null,
    preferred?: CompanyScale
): CompanyScale | null {
    if (!byScale) return null;
    const d = domain.trim().toLowerCase();
    const matches = COMPANY_SCALES.filter((scale) =>
        (byScale[scale] || []).some((entry) => entry.domain.trim().toLowerCase() === d)
    );
    if (matches.length === 0) return null;
    if (preferred && matches.includes(preferred)) return preferred;
    return matches[0];
}

export type CompetitorsByScaleApi = {
    recommended_scale?: string;
    small?: string[];
    medium?: string[];
    large?: string[];
    legacy?: string[];
};

export type CompetitorEntry = {
    domain: string;
    name: string;
    /** Name/keyword aliases carried from the stored "domain[alias1, alias2]" format, if any. */
    aliases?: string[];
};

export function normalizeCompanyScale(raw?: string): CompanyScale {
    const s = (raw || "").toLowerCase().trim().replace(/[\s-]+/g, "_");
    if (s === "small" || s === "smb" || s === "startup" || s === "startups" || s === "sme") return "small";
    if (s === "large" || s === "enterprise") return "large";
    return "medium";
}

export function competitorDomains(entries: CompetitorEntry[]): string[] {
    return entries.map((entry) => entry.domain.trim().toLowerCase()).filter(Boolean);
}

/** Re-encode entries back into the stored "domain[alias1, alias2]" format for persistence. */
export function competitorsToStoredEntries(entries: CompetitorEntry[]): string[] {
    return entries
        .map((entry) => formatCompetitorEntry(entry.domain.trim().toLowerCase(), entry.aliases))
        .filter(Boolean);
}

export function normalizeCompetitorEntry(raw: Partial<CompetitorEntry>): CompetitorEntry | null {
    // Stored entries may carry a "[alias1, alias2]" suffix (see
    // ai-visibility-api entities.EncodeCompetitorEntry) — preserve the aliases
    // alongside the bare domain instead of discarding them.
    const rawDomain = (raw.domain || "").trim().toLowerCase();
    const domain = parseCompetitorDomain(rawDomain);
    if (!domain) return null;
    const aliases = raw.aliases && raw.aliases.length > 0 ? raw.aliases : parseCompetitorAliases(rawDomain);
    return {
        domain,
        name: (raw.name || "").trim() || brandNameFromDomain(domain),
        ...(aliases.length > 0 ? { aliases } : {})
    };
}

export function competitorsFromDomains(domains: string[]): CompetitorEntry[] {
    return domains
        .map((domain) => normalizeCompetitorEntry({ domain }))
        .filter((entry): entry is CompetitorEntry => entry !== null);
}

export function parseCompetitorsFromEnrich(data: { competitors?: string[] }): CompetitorEntry[] {
    return competitorsFromDomains((data.competitors || []).filter(Boolean));
}

export function parseCompetitorsByScale(data: {
    competitors?: string[];
    company_scale?: string;
    competitors_by_scale?: CompetitorsByScaleApi;
}): {
    scale: CompanyScale;
    byScale: Record<CompanyScale, CompetitorEntry[]>;
    selected: CompetitorEntry[];
    hasCatalog: boolean;
} {
    const api = data.competitors_by_scale;
    const scale = normalizeCompanyScale(data.company_scale || api?.recommended_scale);
    const byScale: Record<CompanyScale, CompetitorEntry[]> = {
        small: competitorsFromDomains((api?.small || []).filter(Boolean)),
        medium: competitorsFromDomains((api?.medium || []).filter(Boolean)),
        large: competitorsFromDomains((api?.large || []).filter(Boolean))
    };
    const catalogCount = byScale.small.length + byScale.medium.length + byScale.large.length;
    if (catalogCount === 0) {
        const fallback = competitorsFromDomains((api?.legacy?.length ? api.legacy : data.competitors || []).filter(Boolean));
        byScale[scale] = fallback;
    }
    const selected =
        byScale[scale].length > 0 ? byScale[scale] : parseCompetitorsFromEnrich({ competitors: data.competitors });
    return {
        scale,
        byScale,
        selected,
        hasCatalog: catalogCount > 0
    };
}
